import React, { useCallback, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import RichTextEditor from "../../../components/RichTextEditor";
import { useAxiosInstance } from "../../../services/Interceptor";
import {
  CreatingEvent,
  DeleteEvent,
  GetEventsByEventId,
  UpdateEvent,
  getAttendees,
} from "../../../services/EventsService";
import { useDispatch } from "react-redux";
import { loadEventsAction } from "../../../store/slices/calendarSlice";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ConfirmModal from "../../../components/ConfirmModel";
import {
  DontAddClicked,
  EventButtons,
  IsEditing,
  IsErr,
} from "../../../components/calendar";
import { useResponsiveHeight } from "../../../hooks/useResponsiveHeight.js";

const pad = (n) => String(n ?? 0).padStart(2, "0");

const normalizeEventData = (data) => {
  let { date, startTime, endTime } = data;

  if (date && typeof date === "object") {
    date = `${date.year}-${pad(date.month)}-${pad(date.day)}`;
  }
  if (startTime && typeof startTime === "object") {
    startTime = `${pad(startTime.hour)}:${pad(startTime.minute)}`;
  }
  if (endTime && typeof endTime === "object") {
    endTime = `${pad(endTime.hour)}:${pad(endTime.minute)}`;
  }

  return { ...data, date, startTime, endTime };
};

const getDefaultFormValues = () => ({
  name: "",
  address1: "",
  address2: "",
  city: "",
  state: "",
  zip: "",
  date: "",
  startTime: "",
  endTime: "",
  elevation: 0,
  wind: 0,
  heightComment: "",
  hasPositionDrills: false,
  emailText: "",
  overrideDefaultPricing: false,
  combinePosDrillPrice: 0,
  earlyCombinePrice: 0,
  combinePrice: 0,
  dayOfCombinePrice: 0,
});

function AddEvent({
  addClicked,
  event,
  onEventAdded,
  setSelectedEvent,
  resetKey,
}) {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    getValues,
    trigger,
    clearErrors,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: getDefaultFormValues(),
  });
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(event);
  const apiInstance = useAxiosInstance();
  const dispatch = useDispatch();
  const [err, setErr] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  const name = watch("name");
  const date = watch("date");
  const startTime = watch("startTime");
  const endTime = watch("endTime");
  const overrideDefaultPricing = watch("overrideDefaultPricing", false);

  useEffect(() => {
    setIsFormValid(name && date && startTime && endTime);
  }, [name, date, startTime, endTime]);

  const setFormValues = useCallback(() => {
    if (currentEvent) {
      reset({
        name: currentEvent.name || "",
        address1: currentEvent.address1 || "",
        address2: currentEvent.address2 || "",
        city: currentEvent.city || "",
        state: currentEvent.state || "",
        zip: currentEvent.zip || "",
        date: currentEvent.date || "",
        startTime: currentEvent.startTime || currentEvent.startTimeUTC || "",
        endTime: currentEvent.endTime || currentEvent.endTimeUTC || "",
        elevation: currentEvent.elevation || 0,
        wind: currentEvent.wind || 0,
        heightComment: currentEvent.heightComment || "",
        hasPositionDrills: currentEvent.hasPositionDrills ?? false,
        emailText: currentEvent.emailText || "",
        overrideDefaultPricing: currentEvent.overrideDefaultPricing ?? false,
        combinePrice: currentEvent.combinePrice ?? 0,
        combinePosDrillPrice: currentEvent.combinePosDrillPrice ?? 0,
        earlyCombinePrice: currentEvent.earlyCombinePrice ?? 0,
        dayOfCombinePrice: currentEvent.dayOfCombinePrice ?? 0,
      });

      setIsEditing(true);
      setSelectedEvent(currentEvent.id);
    } else {
      reset(getDefaultFormValues());
      setIsEditing(false);
      setSelectedEvent(null);
    }
  }, [reset, setSelectedEvent, currentEvent]);

  useEffect(() => {
    if (!event?.id) {
      setCurrentEvent(event);
      return;
    }

    Promise.all([
      GetEventsByEventId(apiInstance, event.id),
      getAttendees(apiInstance, event.id).catch(() => []),
    ])
      .then(([data, attendees]) =>
        setCurrentEvent({
          ...normalizeEventData(data),
          totalRegisteredAthletes: Array.isArray(attendees)
            ? attendees.length
            : 0,
        }),
      )
      .catch(() => setCurrentEvent(event));
  }, [event, apiInstance, resetKey]);

  useEffect(() => {
    setFormValues();
  }, [setFormValues]);

  useEffect(() => {
    const priceFields = [
      "combinePosDrillPrice",
      "earlyCombinePrice",
      "combinePrice",
      "dayOfCombinePrice",
    ];
    if (overrideDefaultPricing) {
      trigger(priceFields);
    } else {
      clearErrors(priceFields);
    }
  }, [overrideDefaultPricing, trigger, clearErrors]);

  const onSubmit = async (data) => {
    setLoading(true);
    setErr(null);

    try {
      const pricingFields = [
        {
          key: "combinePosDrillPrice",
          label: "Position Drill Price",
        },
        {
          key: "earlyCombinePrice",
          label: "Early Combine Price",
        },
        {
          key: "combinePrice",
          label: "Two Week Combine Price",
        },
        {
          key: "dayOfCombinePrice",
          label: "Day of Combine Price",
        },
      ];

      const payload = { ...data };

      if (payload.overrideDefaultPricing) {
        const zeroFields = pricingFields.filter(
          ({ key }) => Number(payload[key]) === 0,
        );

        if (zeroFields.length > 0) {
          const zeroFieldLabels = zeroFields
            .map((field) => field.label)
            .join(", ");

          const confirmed = window.confirm(
            `The following override pricing field(s) are set to 0: ${zeroFieldLabels}. Do you want to continue saving with zero pricing?`,
          );

          if (!confirmed) {
            setLoading(false);
            return;
          }
        }
      } else {
        pricingFields.forEach(({ key }) => {
          payload[key] = 0;
        });
      }

      const response = isEditing
        ? await UpdateEvent(apiInstance, currentEvent?.id, payload)
        : await CreatingEvent(apiInstance, payload);

      if (response.status === 200) {
        if (isEditing) {
          toast.success("Event updated successfully!", {
            position: "top-right",
          });
          setCurrentEvent((prev) => ({
            ...prev,
            ...response.data,
          }));
        } else {
          toast.success("Event created successfully!", {
            position: "top-right",
          });
          const newId = response.data.id;
          setCurrentEvent({ ...data, id: newId });
          setIsEditing(true);
        }

        dispatch(loadEventsAction(false));
      } else {
        setErr(response.response.data.error);
      }
    } catch (err) {
      toast.error(err.message || "An error occurred", {
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => setShowDeleteModal(true);

  const handleDeleteConfirm = async () => {
    if (currentEvent) {
      setLoading(true);
      try {
        await DeleteEvent(apiInstance, currentEvent.id);
        toast.success("Event deleted successfully!", {
          position: "top-right",
        });
        setCurrentEvent(null);
        dispatch(loadEventsAction(false));
      } catch (err) {
        toast.error(
          err.message || "An error occurred while deleting the event",
          { position: "top-right" },
        );
      } finally {
        setLoading(false);
        setShowDeleteModal(false);
      }
    }
  };

  const handleDeleteCancel = () => setShowDeleteModal(false);

  const { adjustedHeight } = useResponsiveHeight({
    mobileHeight: 340,
    desktopHeight: 480,
  });

  const todayLocal = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();
  const minDate =
    isEditing && currentEvent?.date && currentEvent.date < todayLocal
      ? currentEvent.date
      : todayLocal;

  const sectionClass = "px-2 py-0";
  const sectionTitleClass =
    "mb-3 text-xs font-semibold uppercase text-gray-400";
  const labelClass = "!text-gray-700 text-sm font-medium pb-1";
  const inputClass =
    "w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none transition focus:border-gray-500 focus:bg-white";
  const disabledInputClass = `${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500`;
  const checkboxClass = "!h-4 !w-4 cursor-pointer rounded border-gray-300";

  const priceRegister = (fieldName, label) =>
    register(fieldName, {
      validate: (value) => {
        const isOverrideEnabled = getValues("overrideDefaultPricing");

        if (!isOverrideEnabled) return true;

        if (value === "" || value === null || value === undefined) {
          return `${label} is required`;
        }

        const parsedValue = Number(value);

        if (Number.isNaN(parsedValue)) {
          return `${label} must be a valid number`;
        }

        if (parsedValue < 0) {
          return `${label} cannot be negative`;
        }

        return true;
      },
      setValueAs: (value) => {
        if (value === "" || value === null || value === undefined) {
          return getValues("overrideDefaultPricing") ? null : 0;
        }
        const parsedValue = Number(value);
        return Number.isNaN(parsedValue) ? 0 : parsedValue;
      },
    });
  const overrideDefaultPricingRegister = register("overrideDefaultPricing", {
    onChange: (e) => {
      if (!e.target.checked) {
        setValue("combinePosDrillPrice", 0);
        setValue("earlyCombinePrice", 0);
        setValue("combinePrice", 0);
        setValue("dayOfCombinePrice", 0);
      }
    },
  });

  return (
    <>
      {addClicked && (
        <div
          className="mx-auto max-w-5xl overflow-y-auto -mr-4 pr-4"
          style={{ height: `${adjustedHeight + 180}px` }}
        >
          <IsErr err={err} setErr={setErr} />
          <form
            className="form-field space-y-6 text-sm"
            onSubmit={handleSubmit(onSubmit)}
          >
            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>Event Information</h2>
              <div>
                <label className={labelClass}>Event Name</label>
                <input
                  type="text"
                  {...register("name")}
                  placeholder="Event Name"
                  className={inputClass}
                  required
                  maxLength={60}
                />
              </div>
            </section>

            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>Location</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>Address 1</label>
                  <input
                    type="text"
                    {...register("address1")}
                    placeholder="Address 1"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Address 2</label>
                  <input
                    type="text"
                    {...register("address2")}
                    placeholder="Address 2"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>City</label>
                  <input
                    type="text"
                    {...register("city")}
                    placeholder="City"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>State</label>
                  <input
                    type="text"
                    {...register("state")}
                    placeholder="State"
                    className={inputClass}
                  />
                </div>
                <div className="sm:max-w-[220px]">
                  <label className={labelClass}>Zip</label>
                  <input
                    type="text"
                    {...register("zip")}
                    placeholder="Zip"
                    className={inputClass}
                  />
                </div>
              </div>
            </section>

            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>Schedule</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2 sm:max-w-[260px]">
                  <label className={labelClass}>Event Date</label>
                  <input
                    type="date"
                    min={minDate}
                    {...register("date", { required: true })}
                    className={`${inputClass} bg-transparent appearance-none`}
                  />
                </div>
                <div>
                  <label className={labelClass}>Start Time</label>
                  <input
                    type="time"
                    {...register("startTime")}
                    placeholder="Start Time"
                    className={`${inputClass} bg-transparent appearance-none`}
                  />
                </div>
                <div>
                  <label className={labelClass}>End Time</label>
                  <input
                    type="time"
                    {...register("endTime")}
                    placeholder="End Time"
                    className={`${inputClass} bg-transparent appearance-none`}
                  />
                </div>
              </div>
            </section>

            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>Environment</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>Elevation</label>
                  <input
                    type="number"
                    step="0.01"
                    min={0}
                    defaultValue={0.0}
                    {...register("elevation")}
                    placeholder="Elevation (feet)"
                    className={inputClass}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "") {
                        e.target.value = 0;
                      }
                    }}
                  />
                </div>
                <div>
                  <label className={labelClass}>Wind</label>
                  <input
                    type="number"
                    step="0.01"
                    min={0}
                    defaultValue={0.0}
                    {...register("wind")}
                    placeholder="Wind (mph)"
                    className={inputClass}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "") {
                        e.target.value = 0;
                      }
                    }}
                  />
                </div>
                <div className="col-span-full">
                  <label className={labelClass}>Comment</label>
                  <input
                    type="text"
                    {...register("heightComment")}
                    placeholder="Comment about height"
                    className={inputClass}
                  />
                </div>
              </div>
            </section>

            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>Athlete Options</h2>
              <div className="flex items-start gap-3 text-gray-700">
                <input
                  type="checkbox"
                  id="hasPositionDrills"
                  {...register("hasPositionDrills")}
                  className={checkboxClass}
                />
                <label
                  htmlFor="hasPositionDrills"
                  className="cursor-pointer text-sm !text-gray-700"
                >
                  Allow athletes to purchase position drills
                </label>
              </div>
            </section>

            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>Email Text</h2>
              <Controller
                name="emailText"
                control={control}
                defaultValue=""
                render={({ field: { value, onChange } }) => (
                  <RichTextEditor value={value} onChange={onChange} />
                )}
              />
            </section>

            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>Custom Combine Pricing</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3 text-gray-700">
                  <input
                    type="checkbox"
                    id="overrideDefaultPricing"
                    {...overrideDefaultPricingRegister}
                    className={checkboxClass}
                  />
                  <label
                    htmlFor="overrideDefaultPricing"
                    className="text-sm !text-gray-700"
                  >
                    Override default pricing for this combine
                  </label>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className={labelClass}>Position Drill Price</label>
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      {...priceRegister(
                        "combinePosDrillPrice",
                        "Position Drill Price",
                      )}
                      placeholder="0.00"
                      className={disabledInputClass}
                      disabled={!overrideDefaultPricing}
                    />
                    {errors.combinePosDrillPrice && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.combinePosDrillPrice.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className={labelClass}>Early Combine Price</label>
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      {...priceRegister(
                        "earlyCombinePrice",
                        "Early Combine Price",
                      )}
                      placeholder="0.00"
                      className={disabledInputClass}
                      disabled={!overrideDefaultPricing}
                    />
                    {errors.earlyCombinePrice && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.earlyCombinePrice.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className={labelClass}>Two Week Combine Price</label>
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      {...priceRegister(
                        "combinePrice",
                        "Two Week Combine Price",
                      )}
                      placeholder="0.00"
                      className={disabledInputClass}
                      disabled={!overrideDefaultPricing}
                    />
                    {errors.combinePrice && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.combinePrice.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className={labelClass}>Day of Combine Price</label>
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      {...priceRegister(
                        "dayOfCombinePrice",
                        "Day of Combine Price",
                      )}
                      placeholder="0.00"
                      className={disabledInputClass}
                      disabled={!overrideDefaultPricing}
                    />
                    {errors.dayOfCombinePrice && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.dayOfCombinePrice.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </section>

            <EventButtons
              handleDelete={handleDelete}
              isEditing={isEditing}
              isFormValid={isFormValid}
              loading={loading}
            />
          </form>
          <IsEditing event={currentEvent} isEditing={isEditing} />
        </div>
      )}

      {!addClicked && <DontAddClicked />}

      <ConfirmModal
        show={showDeleteModal}
        message="Are you sure you want to delete this event?"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      <ToastContainer />
    </>
  );
}

export default AddEvent;
